import { OrderSummary } from '../types';

const CALENDAR_TZ = 'Europe/Dublin';

/**
 * Escape text for use in iCalendar (SUMMARY, DESCRIPTION).
 * Backslash-escape semicolons, commas, and backslashes.
 */
function escapeIcsText(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,');
}

/**
 * Format a Date as iCalendar DATE value (YYYYMMDD).
 */
function formatIcsDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

/** Parse "9:00 AM" / "5:00 PM" to { hours24, minutes }. Returns null if invalid. */
function parseTimeString(timeStr: string): { hours24: number; minutes: number } | null {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const trimmed = timeStr.trim();
  const match12 = trimmed.match(/^(\d+):(\d+)\s*(AM|PM)$/i);
  if (match12) {
    let hour = parseInt(match12[1], 10);
    const minute = parseInt(match12[2], 10) || 0;
    const ampm = match12[3].toUpperCase();
    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    return { hours24: hour, minutes: minute };
  }
  const match24 = trimmed.match(/^(\d+):(\d+)$/);
  if (match24) {
    const hour = parseInt(match24[1], 10);
    const minute = parseInt(match24[2], 10) || 0;
    return { hours24: hour, minutes: minute };
  }
  return null;
}

/** Format date + { hours24, minutes } to iCalendar datetime with TZID (YYYYMMDDTHHmmss). */
function formatIcsDateTime(date: Date, time: { hours24: number; minutes: number }): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(time.hours24).padStart(2, '0');
  const min = String(time.minutes).padStart(2, '0');
  return `${y}${m}${d}T${h}${min}00`;
}

/**
 * Build an iCalendar (.ics) string from orders that have a delivery/collection date.
 * When collection/delivery time windows are present, events are timed; otherwise all-day.
 * Compatible with Apple Calendar and Google Calendar (import or subscribe).
 */
export function ordersToIcs(orders: OrderSummary[], calendarName: string = 'Orders'): string {
  const withDate = orders.filter((o) => o.deliveryDate != null);
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//The Frosting Roon//Orders Calendar//EN',
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${escapeIcsText(calendarName)}`,
    'METHOD:PUBLISH'
  ];

  for (const order of withDate) {
    const d = order.deliveryDate!;
    const dateObj = d instanceof Date ? d : new Date(d);
    const dateStr = formatIcsDate(dateObj);
    const type = order.deliveryOption === 'delivery' ? 'Delivery' : 'Collection';
    const summary = `Order #${order.orderNumber} – ${type}`;
    const descParts = [
      `${order.customerName} – ${order.customerEmail}`,
      order.itemNames?.length ? order.itemNames.join(', ') : `${order.itemCount} item(s)`,
      `Total: €${order.total.toFixed(2)}`
    ];
    const description = descParts.join('\\n');
    const uid = `order-${order.id}-${dateStr}@frosting-roon`;

    const isDelivery = order.deliveryOption === 'delivery';
    const startTimeStr = isDelivery ? order.deliveryStartTime : order.collectionStartTime;
    const endTimeStr = isDelivery ? order.deliveryEndTime : order.collectionEndTime;
    const startTime = startTimeStr ? parseTimeString(startTimeStr) : null;
    const endTime = endTimeStr ? parseTimeString(endTimeStr) : null;
    const hasTimes = startTime && endTime;

    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${uid}`);
    lines.push(`DTSTAMP:${formatIcsDate(new Date())}T120000Z`);

    if (hasTimes) {
      lines.push(`DTSTART;TZID=${CALENDAR_TZ}:${formatIcsDateTime(dateObj, startTime)}`);
      lines.push(`DTEND;TZID=${CALENDAR_TZ}:${formatIcsDateTime(dateObj, endTime)}`);
    } else {
      lines.push(`DTSTART;VALUE=DATE:${dateStr}`);
      const nextDay = new Date(dateObj);
      nextDay.setDate(nextDay.getDate() + 1);
      lines.push(`DTEND;VALUE=DATE:${formatIcsDate(nextDay)}`);
    }

    lines.push(`SUMMARY:${escapeIcsText(summary)}`);
    lines.push(`DESCRIPTION:${escapeIcsText(description)}`);
    lines.push('END:VEVENT');
  }

  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/**
 * Trigger a file download of an .ics string.
 */
export function downloadIcs(icsContent: string, filename: string = 'orders-calendar.ics'): void {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
